import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Search, Eye, Package as PackageIcon, Trash2, Building2, Mail, User, CreditCard, Calendar, MapPin, Phone, X, UserPlus, ArrowLeft } from 'lucide-react';
import SuperAddDoctors from './super_add_doctors';
import { BASE_URL } from '../config';
import { notify } from '../utils/toast';

export default function HospitalList({ onBack }) {
  const [hospitals, setHospitals] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingHospital, setEditingHospital] = useState(null);
  const [viewingHospital, setViewingHospital] = useState(null);
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showAddDoctors, setShowAddDoctors] = useState(false);
  const [selectedHospitalForDoctors, setSelectedHospitalForDoctors] = useState(null);
  const [form, setForm] = useState({
    name: '',
    address: '',
    contact_number: '',
    email: '',
    established_year: '',
  });

  const fetchHospitals = async () => {
    const token = localStorage.getItem('accessToken');
    try {
      const res = await axios.get(`${BASE_URL}/api/hospitals/list-with-details/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHospitals(res.data);
    } catch (err) {
      console.error('Fetch error:', err.response?.data || err.message);
    }
  };

  const fetchPackages = async () => {
    const token = localStorage.getItem('accessToken');
    try {
      const res = await axios.get(`${BASE_URL}/api/hospitals/packages/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPackages(res.data);
    } catch (err) {
      console.error('Fetch packages error:', err.response?.data || err.message);
    }
  };

  useEffect(() => {
    fetchHospitals();
    fetchPackages();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEditClick = (hospital) => {
    setEditingHospital(hospital.user_id);
    setForm({
      name: hospital.name,
      address: hospital.address,
      contact_number: hospital.contact_number,
      email: hospital.email,
      established_year: hospital.established_year,
    });
  };

  const handleUpdate = async () => {
    const token = localStorage.getItem('accessToken');
    try {
      await axios.put(
        `${BASE_URL}/api/hospitals/superadmin/`,
        { ...form, user_id: editingHospital },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify.success('Hospital updated successfully');
      setEditingHospital(null);
      fetchHospitals();
    } catch (err) {
      console.error('Update error:', err.response?.data || err.message);
      notify.error('Error updating hospital');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this hospital?')) return;
    
    const token = localStorage.getItem('accessToken');
    try {
      await axios.delete(`${BASE_URL}/api/hospitals/superadmin/`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { user_id: userId },
      });
      notify.success('Hospital deleted successfully');
      fetchHospitals();
    } catch (err) {
      console.error('Delete error:', err.response?.data || err.message);
      notify.error('Error deleting hospital');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleViewMore = (hospital) => {
    setViewingHospital(hospital);
  };

  const handleChangePackage = (hospital) => {
    setSelectedHospital(hospital);
    setSelectedPackage(hospital.package_id);
    setShowPackageModal(true);
  };

  const handlePackageUpdate = async () => {
    if (!selectedPackage) {
      notify.error('Please select a package');
      return;
    }

    const token = localStorage.getItem('accessToken');
    try {
      await axios.put(
        `${BASE_URL}/api/superadmin/${selectedHospital.user_id}/package/`,
        { package_id: selectedPackage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify.success('Package updated successfully');
      setShowPackageModal(false);
      fetchHospitals();
    } catch (err) {
      console.error('Package update error:', err.response?.data || err.message);
      notify.error('Error updating package');
    }
  };

  const handleAddDoctors = (hospital) => {
    setSelectedHospitalForDoctors(hospital);
    setShowAddDoctors(true);
  };

  const handleBackToHospitals = () => {
    setShowAddDoctors(false);
    setSelectedHospitalForDoctors(null);
  };

  const filteredHospitals = hospitals.filter((h) =>
    Object.values(h)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  // If showing add doctors view, render that instead
  if (showAddDoctors && selectedHospitalForDoctors) {
    return (
      <SuperAddDoctors
        hospitalId={selectedHospitalForDoctors.user_id}
        hospitalName={selectedHospitalForDoctors.username}
        onBack={handleBackToHospitals}
      />
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Back Button */}
      {onBack && (
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-medium">Back</span>
        </motion.button>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-medical-500 to-medical-600 rounded-xl shadow-sm p-6 text-white">
        <div className="flex items-center gap-3">
          <Building2 className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">Hospital Management</h2>
            <p className="text-medical-100 text-sm">View and manage all registered hospitals</p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
          <input
            type="text"
            placeholder="Search hospitals by username, email, package..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none transition-all"
          />
        </div>
      </div>

      {/* Hospitals Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          {filteredHospitals.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gradient-to-r from-medical-500 to-medical-600 text-white">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold">Username</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold">Email</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold">Package</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold">Credits</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold">Days</th>
                  <th className="text-center py-4 px-6 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredHospitals.map((h, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="border-b border-slate-200 hover:bg-slate-100 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-primary-400" />
                        </div>
                        <span className="text-slate-900 font-medium">{h.username}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4 text-slate-600" />
                        {h.email}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-500/20 text-primary-300">
                        <PackageIcon className="w-4 h-4 mr-1.5" />
                        {h.package_name}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-1.5 text-slate-700">
                        <CreditCard className="w-4 h-4 text-slate-600" />
                        <span className="font-medium">{h.credit_limit}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-1.5 text-slate-700">
                        <Calendar className="w-4 h-4 text-slate-600" />
                        <span className="font-medium">{h.days_limit}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewMore(h)}
                          className="px-3 py-2 text-sm text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors flex items-center gap-1.5 font-medium"
                          title="View More"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button
                          onClick={() => handleAddDoctors(h)}
                          className="px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors flex items-center gap-1.5 font-medium"
                          title="Add Doctors"
                        >
                          <UserPlus className="w-4 h-4" />
                          Add Doc
                        </button>
                        <button
                          onClick={() => handleChangePackage(h)}
                          className="px-3 py-2 text-sm text-slate-900 bg-primary-500/100 hover:bg-medical-600 rounded-lg transition-colors flex items-center gap-1.5 font-medium"
                          title="Change Package"
                        >
                          <PackageIcon className="w-4 h-4" />
                          Package
                        </button>
                        <button
                          onClick={() => handleDelete(h.user_id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Hospital"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 text-slate-700 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No hospitals found</p>
              <p className="text-slate-600 text-sm mt-2">Try adjusting your search criteria</p>
            </div>
          )}
        </div>
      </div>

      {/* View More Modal */}
      <AnimatePresence>
        {viewingHospital && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setViewingHospital(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-medical-500 to-medical-600 text-white p-6 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-700/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">Hospital Details</h3>
                      <p className="text-medical-100 text-sm">Complete information</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setViewingHospital(null)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* User Information */}
                <div className="bg-white rounded-lg p-5">
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-primary-400" />
                    User Account Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Username</p>
                      <p className="text-base font-medium text-slate-900">{viewingHospital.username}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Email</p>
                      <p className="text-base font-medium text-slate-900 flex items-center gap-1">
                        <Mail className="w-4 h-4 text-slate-600" />
                        {viewingHospital.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Package Information */}
                <div className="bg-primary-500/10 rounded-lg p-5">
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <PackageIcon className="w-5 h-5 text-primary-400" />
                    Package Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Package Name</p>
                      <p className="text-lg font-semibold text-primary-400">{viewingHospital.package_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Price</p>
                      <p className="text-lg font-semibold text-slate-900">${viewingHospital.package_price}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Credit Limit</p>
                      <p className="text-base font-medium text-slate-900 flex items-center gap-1">
                        <CreditCard className="w-4 h-4 text-slate-600" />
                        {viewingHospital.credit_limit} Credits
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Days Limit</p>
                      <p className="text-base font-medium text-slate-900 flex items-center gap-1">
                        <Calendar className="w-4 h-4 text-slate-600" />
                        {viewingHospital.days_limit} Days
                      </p>
                    </div>
                  </div>
                </div>

                {/* Hospital Information */}
                {viewingHospital.name ? (
                  <div className="bg-primary-500/10 rounded-lg p-5">
                    <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-primary-400" />
                      Hospital Information
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Hospital Name</p>
                        <p className="text-lg font-semibold text-slate-900">{viewingHospital.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Address</p>
                        <p className="text-base font-medium text-slate-900 flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-slate-600" />
                          {viewingHospital.address}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-600 mb-1">Contact Number</p>
                          <p className="text-base font-medium text-slate-900 flex items-center gap-1">
                            <Phone className="w-4 h-4 text-slate-600" />
                            {viewingHospital.contact_number}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600 mb-1">Hospital Email</p>
                          <p className="text-base font-medium text-slate-900 flex items-center gap-1">
                            <Mail className="w-4 h-4 text-slate-600" />
                            {viewingHospital.hospital_email}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 mb-1">Established Year</p>
                        <p className="text-base font-medium text-slate-900 flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-slate-600" />
                          {viewingHospital.established_year}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-center">
                    <Building2 className="w-12 h-12 text-amber-400 mx-auto mb-2" />
                    <p className="text-amber-700 font-medium">Hospital details not yet provided</p>
                    <p className="text-amber-600 text-sm mt-1">The hospital needs to complete their profile</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Change Package Modal */}
      <AnimatePresence>
        {showPackageModal && selectedHospital && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPackageModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-medical-500 to-medical-600 text-white p-6 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-700/20 rounded-lg flex items-center justify-center">
                      <PackageIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">Change Package</h3>
                      <p className="text-medical-100 text-sm">Select a new package for this hospital</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPackageModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Hospital Information */}
                <div className="bg-white rounded-lg p-5">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary-400" />
                    Hospital Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Username</p>
                      <p className="text-base font-medium text-slate-900">{selectedHospital.username}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Email</p>
                      <p className="text-base font-medium text-slate-900 flex items-center gap-1">
                        <Mail className="w-4 h-4 text-slate-600" />
                        {selectedHospital.email}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-slate-600 mb-1">Current Package</p>
                      <div className="inline-flex items-center px-4 py-2 rounded-lg bg-primary-500/20 text-primary-300 font-medium">
                        <PackageIcon className="w-4 h-4 mr-2" />
                        {selectedHospital.package_name}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Package Selection */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <PackageIcon className="w-5 h-5 text-primary-400" />
                    Select New Package
                  </h4>
                  <div className="grid grid-cols-1 gap-4 max-h-96 overflow-y-auto pr-2">
                    {packages.map((pkg) => (
                      <motion.div
                        key={pkg.package_ID}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setSelectedPackage(pkg.package_ID)}
                        className={`p-5 border-2 rounded-xl cursor-pointer transition-all ${
                          selectedPackage === pkg.package_ID
                            ? 'border-medical-500 bg-primary-500/10 shadow-md'
                            : 'border-slate-200 hover:border-medical-300 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className="text-lg font-semibold text-slate-900">{pkg.package_name}</h5>
                              {selectedPackage === pkg.package_ID && (
                                <span className="px-2 py-0.5 bg-primary-500/100 text-white text-xs rounded-full font-medium">
                                  Selected
                                </span>
                              )}
                            </div>
                            <p className="text-3xl font-bold text-primary-400 mb-3">${pkg.price}</p>
                            <div className="flex items-center gap-4 text-sm text-slate-600">
                              <div className="flex items-center gap-1.5">
                                <CreditCard className="w-4 h-4 text-slate-600" />
                                <span><span className="font-semibold text-slate-700">{pkg.credit_limit}</span> Credits</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <Calendar className="w-4 h-4 text-slate-600" />
                                <span><span className="font-semibold text-slate-700">{pkg.days_limit}</span> Days</span>
                              </div>
                            </div>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            selectedPackage === pkg.package_ID
                              ? 'border-medical-500 bg-primary-500/100'
                              : 'border-slate-300'
                          }`}>
                            {selectedPackage === pkg.package_ID && (
                              <div className="w-2 h-2 bg-white rounded-full"></div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <button
                    onClick={() => setShowPackageModal(false)}
                    className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePackageUpdate}
                    disabled={!selectedPackage}
                    className="flex-1 px-6 py-3 bg-primary-500/100 text-white font-medium rounded-lg hover:bg-medical-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <PackageIcon className="w-5 h-5" />
                    Confirm Change
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
